import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSignin } from "../hooks/useLogin";
import { css } from "@emotion/react";
import { BeatLoader } from "react-spinners";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";
import { NODE_API_ENDPOINT } from "../utils/utils";

import { GoogleLogin } from "@react-oauth/google";
import { useAppContext } from "../Contexts/AppContext";
import { useAuthcontext } from "../Contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import socket from "../connection";

const override = css`
  display: block;
  margin: 0 auto;
  border-color: red;
`;

const LoginForm = () => {
  const { signin, isLoadingL, errorL } = useSignin();
  const { setRoom, setType } = useAppContext();
  const { dispatch } = useAuthcontext();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loginFromGoogle = async (credentialResponse) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${NODE_API_ENDPOINT}/api/googleAuth/callback-login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: credentialResponse.credential }),
        }
      );

      if (!response.ok) {
        toast.error("Failed to login with Google. Please try again.");
        setLoading(false);
        return;
      }
      const json = await response.json();
      console.log(json);

      // save the user to local storage
      localStorage.setItem("TheatorUser", JSON.stringify(json));

      // update the auth context
      dispatch({ type: "LOGIN", payload: json });
      setRoom(json.room);
      setType(json.sessionType);

      if (json.sessionType === "youTube") {
        if (json?.room) {
          socket.emit("joinRoom", { room: json?.room });
        }
      } else {
        if (json?.room) {
          socket.emit("manualSessions-joinRoom", { room: json?.room });
        }
      }

      setLoading(false);
      navigate("/");

      toast.success("Welcome Back!");
    } catch (error) {
      setLoading(false);
      toast.error("Failed to login with Google. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <Helmet>
        <title>Login - SyncMovie</title>
        <meta
          name="description"
          content="Login to SyncMovie and start watching movies with your friends online. Enjoy synchronized movie streaming and stay connected."
        />
        <meta
          name="keywords"
          content="SyncMovie login, online movie platform login, watch movies with friends, synchronized movie streaming login"
        />
        <link
          rel="canonical"
          href="https://syncmovie-watch.netlify.app/login"
        />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Login - SyncMovie" />
        <meta
          property="og:description"
          content="Login to SyncMovie and watch movies together with friends in sync. Get access to your personal movie theater."
        />
        <meta
          property="og:url"
          content="https://syncmovie-watch.netlify.app/login"
        />
        <meta
          property="og:image"
          content="https://syncmovie-watch.netlify.app/static/login-social-banner.png"
        />
        <meta property="og:type" content="website" />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Login - SyncMovie" />
        <meta
          name="twitter:description"
          content="Sign in to SyncMovie and start enjoying movies with synchronized playback online with friends."
        />
        <meta
          name="twitter:image"
          content="https://syncmovie-watch.netlify.app/static/login-social-banner.png"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
        {
          "@context": "https://schema.org",
          "@type": "LoginPage",
          "name": "Login - SyncMovie",
          "url": "https://syncmovie-watch.netlify.app/login"
        }
      `}
        </script>

        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      {/* Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/15 blur-[120px] rounded-full"></div>
        <div className="absolute bottom-[10%] right-[-5%] w-[45%] h-[45%] bg-purple-600/15 blur-[120px] rounded-full"></div>
      </div>

      <div className="max-w-md w-full space-y-8 bg-slate-900/40 backdrop-blur-xl border border-slate-800/80 p-8 md:p-10 rounded-3xl shadow-2xl relative z-10">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-100 to-blue-400">
            Welcome Back
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Watch movies in sync with your favorite people
          </p>
        </div>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={Yup.object({
            email: Yup.string().required("Required"),
            password: Yup.string().required("Required"),
          })}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            signin(values.email.trim(), values.password.trim());
            if (errorL) {
              console.log(errorL);
            }
            setSubmitting(false);
            resetForm();
          }}
        >
          {({ isSubmitting }) => (
            <Form className="mt-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="sr-only">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                    <Field
                      type="text"
                      name="email"
                      className="block w-full pl-11 pr-4 py-3 bg-slate-950/40 border border-slate-800/80 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/80 transition-all text-sm"
                      placeholder="Email address"
                    />
                  </div>
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-rose-400 text-xs mt-1.5 ml-1 font-medium"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="sr-only">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <Field
                      type="password"
                      name="password"
                      className="block w-full pl-11 pr-4 py-3 bg-slate-950/40 border border-slate-800/80 rounded-xl text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/80 transition-all text-sm"
                      placeholder="Password"
                    />
                  </div>
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-rose-400 text-xs mt-1.5 ml-1 font-medium"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-500 transition-all shadow-lg shadow-blue-500/20 active:scale-[0.98] disabled:opacity-50"
                >
                  {isLoadingL ? (
                    <BeatLoader
                      color="#ffffff"
                      loading={true}
                      css={override}
                      size={8}
                    />
                  ) : (
                    "Sign in"
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <div className="relative flex items-center justify-center my-6">
          <div className="border-t border-slate-800 w-full"></div>
          <span className="absolute bg-slate-900/90 backdrop-blur-md px-3 text-xs text-slate-500 uppercase tracking-widest font-mono">
            Or continue with
          </span>
        </div>

        <div className="flex justify-center">
          <div
            className="w-full flex justify-center h-10 items-center rounded-xl"
            style={{
              backgroundColor: `${loading ? "rgba(0, 0, 0, 0.5)" : "transparent"}`,
            }}
          >
            {loading ? (
              <BeatLoader
                color="#ffffff"
                loading={true}
                css={override}
                size={10}
              />
            ) : (
              <GoogleLogin
                width="340px"
                theme="filled_blue"
                shape="pill"
                onSuccess={loginFromGoogle}
                onError={(error) => {
                  console.log("Login Failed", error);
                  toast.error("Login Failed");
                }}
                useOneTap
              />
            )}
          </div>
        </div>

        <p className="text-center text-sm text-slate-400 mt-6">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
